'use client';

import { useCallback } from 'react';
import { useTranslations } from 'next-intl';
import RangeToggleGroup from '@/src/components/RangeToggleGroup';
import type { FilterState } from '@/src/lib/filters/filterTypes';

// Selecting '4' as maxValue means open-ended (4+ beds); anything beyond maps to null in FilterState
const BEDS_OPEN_MAX = '4';

type BedroomsRangeToggleGroupProps = {
  bedrooms: FilterState['bedrooms'];
  onChange: (next: FilterState['bedrooms']) => void;
};

export default function BedroomsRangeToggleGroup({ bedrooms, onChange }: BedroomsRangeToggleGroupProps) {
  const tFilters = useTranslations('filters');

  const bedOptions = [
    { value: 'any', label: tFilters('any'), testId: 'filter-beds-any' },
    { value: '0', label: tFilters('studio'), testId: 'filter-beds-studio' },
    { value: '1', label: '1', testId: 'filter-beds-1' },
    { value: '2', label: '2', testId: 'filter-beds-2' },
    { value: '3', label: '3', testId: 'filter-beds-3' },
    { value: '4', label: '4+', testId: 'filter-beds-4-plus' },
  ];

  // FilterState → RangeToggleGroup: null max maps to BEDS_OPEN_MAX so the last button shows as selected
  const minValue: string | null = bedrooms.min === null ? null : String(bedrooms.min);
  const maxValue: string | null =
    bedrooms.min === null
      ? null
      : bedrooms.max === null
        ? BEDS_OPEN_MAX
        : String(Math.min(bedrooms.max, parseInt(BEDS_OPEN_MAX, 10)));

  const handleChange = useCallback(
    (minVal: string | null, maxVal: string | null) => {
      if (minVal === null) {
        onChange({ min: null, max: null });
        return;
      }
      const min = parseInt(minVal, 10);
      const max = maxVal === null || maxVal === BEDS_OPEN_MAX ? null : parseInt(maxVal, 10);
      onChange({ min, max });
    },
    [onChange],
  );

  return (
    <RangeToggleGroup
      options={bedOptions}
      minValue={minValue}
      maxValue={maxValue}
      onChange={handleChange}
      aria-label={tFilters('numberOfBedrooms')}
      clearValue="any"
      fluid
    />
  );
}
